import type {
  FilamentWithRelations,
  TenantFilamentWithDetails,
  FilamentQuery,
  CreateFilamentInput,
  UpdateFilamentInput,
  AddFilamentToLibraryInput,
  UpdateTenantFilamentInput,
  FilamentType,
  FilamentManufacturer,
  FilamentFinish,
  EmbeddedMaterial,
} from '@maker-toolchest/shared';
import { api } from '@/lib/api';

// ============================================================================
// FILAMENT LIBRARY (System + Custom)
// ============================================================================

export interface FilamentLibraryListResponse {
  items: FilamentWithRelations[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const filamentLibraryApi = {
  /**
   * Search the filament library
   */
  search: async (params?: Partial<FilamentQuery>): Promise<FilamentLibraryListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.typeId) searchParams.set('typeId', params.typeId);
    if (params?.manufacturerId) searchParams.set('manufacturerId', params.manufacturerId);
    if (params?.finishId) searchParams.set('finishId', params.finishId);
    if (params?.embeddedMaterialId) searchParams.set('embeddedMaterialId', params.embeddedMaterialId);
    if (params?.diameter) searchParams.set('diameter', String(params.diameter));
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params?.includeSystem !== undefined) searchParams.set('includeSystem', String(params.includeSystem));
    if (params?.includeCustom !== undefined) searchParams.set('includeCustom', String(params.includeCustom));

    const query = searchParams.toString();
    const response = await api.get<FilamentWithRelations[]>(`/filaments/library${query ? `?${query}` : ''}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch filament library');
    }
    return {
      items: response.data,
      pagination: response.meta?.pagination || {
        page: params?.page || 1,
        limit: params?.limit || 20,
        total: response.data.length,
        totalPages: 1,
      },
    };
  },

  /**
   * Get a single filament from the library
   */
  get: async (id: string): Promise<FilamentWithRelations> => {
    const response = await api.get<FilamentWithRelations>(`/filaments/library/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch filament');
    }
    return response.data;
  },

  /**
   * Create a custom filament
   */
  create: async (data: CreateFilamentInput): Promise<FilamentWithRelations> => {
    const response = await api.post<FilamentWithRelations>('/filaments/library', data);
    if (!response.success || !response.data) {
      throw new Error('Failed to create filament');
    }
    return response.data;
  },

  /**
   * Update a custom filament
   */
  update: async (id: string, data: UpdateFilamentInput): Promise<FilamentWithRelations> => {
    const response = await api.patch<FilamentWithRelations>(`/filaments/library/${id}`, data);
    if (!response.success || !response.data) {
      throw new Error('Failed to update filament');
    }
    return response.data;
  },

  /**
   * Delete a custom filament
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/filaments/library/${id}`);
  },
};

// ============================================================================
// TENANT FILAMENTS (My Library)
// ============================================================================

export interface TenantFilamentListResponse {
  items: TenantFilamentWithDetails[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface TenantFilamentQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  typeId?: string;
  manufacturerId?: string;
  sortBy?: 'addedAt' | 'name' | 'manufacturer' | 'type';
  sortOrder?: 'asc' | 'desc';
}

export const tenantFilamentApi = {
  /**
   * List filaments in tenant's library
   */
  list: async (params?: TenantFilamentQueryParams): Promise<TenantFilamentListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.typeId) searchParams.set('typeId', params.typeId);
    if (params?.manufacturerId) searchParams.set('manufacturerId', params.manufacturerId);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const query = searchParams.toString();
    const response = await api.get<TenantFilamentWithDetails[]>(`/filaments${query ? `?${query}` : ''}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch filaments');
    }
    return {
      items: response.data,
      pagination: response.meta?.pagination || {
        page: params?.page || 1,
        limit: params?.limit || 20,
        total: response.data.length,
        totalPages: 1,
      },
    };
  },

  /**
   * Get a single tenant filament
   */
  get: async (id: string): Promise<TenantFilamentWithDetails> => {
    const response = await api.get<TenantFilamentWithDetails>(`/filaments/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch filament');
    }
    return response.data;
  },

  /**
   * Add a filament to tenant's library
   */
  add: async (data: AddFilamentToLibraryInput): Promise<TenantFilamentWithDetails> => {
    const response = await api.post<TenantFilamentWithDetails>('/filaments/add', data);
    if (!response.success || !response.data) {
      throw new Error('Failed to add filament to library');
    }
    return response.data;
  },

  /**
   * Update tenant-specific notes
   */
  update: async (id: string, data: UpdateTenantFilamentInput): Promise<TenantFilamentWithDetails> => {
    const response = await api.patch<TenantFilamentWithDetails>(`/filaments/${id}`, data);
    if (!response.success || !response.data) {
      throw new Error('Failed to update filament');
    }
    return response.data;
  },

  /**
   * Remove filament from tenant's library
   */
  remove: async (id: string): Promise<void> => {
    await api.delete(`/filaments/${id}`);
  },
};

// ============================================================================
// LOOKUP TABLES
// ============================================================================

export const lookupApi = {
  /**
   * Get all filament types
   */
  getTypes: async (): Promise<FilamentType[]> => {
    const response = await api.get<FilamentType[]>('/filament-types');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch filament types');
    }
    return response.data;
  },

  /**
   * Create a new filament type
   */
  createType: async (name: string): Promise<FilamentType> => {
    const response = await api.post<FilamentType>('/filament-types', { name });
    if (!response.success || !response.data) {
      throw new Error('Failed to create filament type');
    }
    return response.data;
  },

  /**
   * Get all filament manufacturers
   */
  getManufacturers: async (): Promise<FilamentManufacturer[]> => {
    const response = await api.get<FilamentManufacturer[]>('/filament-manufacturers');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch manufacturers');
    }
    return response.data;
  },

  /**
   * Create a new filament manufacturer
   */
  createManufacturer: async (name: string): Promise<FilamentManufacturer> => {
    const response = await api.post<FilamentManufacturer>('/filament-manufacturers', { name });
    if (!response.success || !response.data) {
      throw new Error('Failed to create manufacturer');
    }
    return response.data;
  },

  /**
   * Get all filament finishes
   */
  getFinishes: async (): Promise<FilamentFinish[]> => {
    const response = await api.get<FilamentFinish[]>('/filament-finishes');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch finishes');
    }
    return response.data;
  },

  /**
   * Create a new filament finish
   */
  createFinish: async (name: string): Promise<FilamentFinish> => {
    const response = await api.post<FilamentFinish>('/filament-finishes', { name });
    if (!response.success || !response.data) {
      throw new Error('Failed to create finish');
    }
    return response.data;
  },

  /**
   * Get all embedded materials
   */
  getEmbeddedMaterials: async (): Promise<EmbeddedMaterial[]> => {
    const response = await api.get<EmbeddedMaterial[]>('/embedded-materials');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch embedded materials');
    }
    return response.data;
  },

  /**
   * Create a new embedded material
   */
  createEmbeddedMaterial: async (name: string): Promise<EmbeddedMaterial> => {
    const response = await api.post<EmbeddedMaterial>('/embedded-materials', { name });
    if (!response.success || !response.data) {
      throw new Error('Failed to create embedded material');
    }
    return response.data;
  },
};
